import React, { createContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Slate } from 'slate-react';

import { TranslatedTooltipWrapper } from 'src/components/TranslatedTooltipWrapper';
import {
  DEFAULT_SLATE_VALUE,
  hasSlateContentChangeOperation,
  hasSlateSelectionOperation,
} from 'src/lib/slate';

export const CustomSlateContext = createContext();

export const CustomSlate = ({
  editor,
  initialValue,
  onChange,
  onSelectionChange,
  onValueChange,
  children,
}) => {
  const [isLinkInsertOpen, setIsLinkInsertOpen] = useState(false);
  const openLinkInsertPopup = useCallback(() => setIsLinkInsertOpen(true), []);
  const closeLinkInsertPopup = useCallback(() => setIsLinkInsertOpen(false), []);

  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const openLinkPopup = useCallback(() => setIsLinkOpen(true), []);
  const closeLinkPopup = useCallback(() => setIsLinkOpen(false), []);

  const contextValue = useMemo(
    () => ({
      isLinkInsertOpen,
      openLinkInsertPopup,
      closeLinkInsertPopup,
      isLinkOpen,
      openLinkPopup,
      closeLinkPopup,
    }),
    [
      isLinkInsertOpen,
      openLinkInsertPopup,
      closeLinkInsertPopup,
      isLinkOpen,
      openLinkPopup,
      closeLinkPopup,
    ]
  );

  const handleChange = useCallback(
    (newValue) => {
      if (onChange) {
        onChange(newValue);
      }

      if (onValueChange && hasSlateContentChangeOperation(editor.operations)) {
        onValueChange(newValue);
      }

      if (onSelectionChange && hasSlateSelectionOperation(editor.operations)) {
        onSelectionChange(editor.selection);
      }
    },
    [editor, onChange, onValueChange, onSelectionChange]
  );

  return (
    <CustomSlateContext.Provider value={contextValue}>
      <TranslatedTooltipWrapper>
        <Slate
          editor={editor}
          initialValue={initialValue || DEFAULT_SLATE_VALUE}
          onChange={handleChange}
        >
          {children}
        </Slate>
      </TranslatedTooltipWrapper>
    </CustomSlateContext.Provider>
  );
};

CustomSlate.propTypes = {
  editor: PropTypes.shape().isRequired,
  children: PropTypes.node.isRequired,
  initialValue: PropTypes.arrayOf(PropTypes.shape()),
  onChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onValueChange: PropTypes.func,
};

CustomSlate.defaultProps = {
  initialValue: null,
  onChange: null,
  onSelectionChange: null,
  onValueChange: null,
};
